<template>
  <div class="create-bill page--create-bill">
    <div class="page__header">
      <div class="page__title">
        <router-link :to="backLinkRoute" class="page__back-link">
          <arrow-button
            class="page__back-link-icon"
            :rotated180="true"
          ></arrow-button>
        </router-link>
        <div class="page__title-text">
          {{ $t('create-bill.page.title') }}
        </div>
      </div>
    </div>
    <div class="create-bill__form">
      <div
        class="create-bill__form-loader"
        v-if="billCreating || isPageLoading"
      >
        <loader></loader>
      </div>
      <div class="create-bill__form-fields">
        <div class="create-bill__form-field">
          <text-input
            v-if="shouldProvidePhoneEmail"
            v-model:value="billData.phoneEmail"
            :title="$t('create-bill.fields.phone-email')"
            :placeholder="$t('create-bill.fields.phone-email-placeholder')"
          ></text-input>
        </div>
        <div class="create-bill__form-field">
          <text-input
            v-model:value="billData.firstName"
            :title="$t('create-bill.fields.first-name')"
            :placeholder="$t('create-bill.fields.first-name-placeholder')"
          ></text-input>
        </div>
        <div class="create-bill__form-field">
          <text-input
            v-model:value="billData.lastName"
            :title="$t('create-bill.fields.last-name')"
            :placeholder="$t('create-bill.fields.last-name-placeholder')"
          ></text-input>
        </div>
        <div class="create-bill__form-field create-bill__form-field--row">
          <custom-select
            class="create-bill__form-field-small"
            v-model:value="billData.lang"
            mode="big"
            value-key="id"
            :title="$t('create-bill.fields.lang')"
            :options="langOptions"
          ></custom-select>
          <text-input
            v-model:value="billData.billNumber"
            :placeholder="$t('create-bill.fields.bill-number-placeholder')"
            :title="$t('create-bill.fields.bill-number')"
          ></text-input>
        </div>
        <div class="create-bill__form-field create-bill__form-field--row">
          <custom-select
            class="create-bill__form-field-small"
            v-model:value="billData.currency"
            mode="big"
            value-key="id"
            :title="$t('create-bill.fields.currency')"
            :options="currencyOptions"
          ></custom-select>
          <text-input
            v-model:value="billData.sum"
            :mask="numberMaskOptions"
            :placeholder="$t('create-bill.fields.sum-placeholder')"
            :title="$t('create-bill.fields.sum')"
          ></text-input>
        </div>
        <div class="create-bill__form-field" v-if="addBillType">
          <custom-select
            :options="billTypeOptions"
            v-model:value="billData.billType"
            :title="billTypeTitle"
            value-key="id"
            mode="big"
          ></custom-select>
        </div>
        <div class="create-bill__form-field">
          <text-input
            v-model:value="billData.purpose"
            :title="$t('create-bill.fields.purpose')"
            :hint="$t('create-bill.fields.purpose-hint')"
            :placeholder="$t('create-bill.fields.purpose-placeholder')"
          ></text-input>
        </div>
        <div class="create-bill__form-field">
          <custom-date-picker
            v-model:value="billData.createdAt"
            :disabled="true"
            :label="$t('create-bill.fields.created-at')"
            mode="big"
          ></custom-date-picker>
        </div>
        <div class="create-bill__form-field">
          <custom-date-picker
            v-model:value="billData.expAt"
            :disabled-date-handler="disabledDateHandler"
            :label="$t('create-bill.fields.exp-at')"
            mode="big"
          ></custom-date-picker>
        </div>
        <div class="create-bill__form-field">
          <text-input
            v-model:value="billData.comment"
            :hint="$t('create-bill.fields.comment-hint')"
            :title="$t('create-bill.fields.comment')"
            :placeholder="$t('create-bill.fields.comment-placeholder')"
          ></text-input>
        </div>
      </div>
      <div class="create-bill__form-actions">
        <primary-button
          class="create-bill__form-action"
          :disabled="
            billCreating ||
            v$.$silentErrors.length > 0 ||
            billData.sum <= 0 ||
            !phoneEmailValidation
          "
          :label="$t('create-bill.actions.create')"
          @click="createBill"
        ></primary-button>
      </div>
    </div>
  </div>
</template>

<script>
import ArrowButton from '@vue-app/components/base/button/ArrowButton.vue'
import TextInput from '@vue-app/components/base/input/TextInput.vue'
import PrimaryButton from '@vue-app/components/base/button/PrimaryButton.vue'
import CustomSelect from '@vue-app/components/base/input/CustomSelect.vue'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import CustomDatePicker from '@vue-app/components/base/input/CustomDatePicker.vue'
import moment from 'moment'
import CheckboxInput from '@vue-app/components/base/input/CheckboxInput.vue'
import { RouteHelper } from '@js-app/services/helpers/routes/routes-helper'
import { CompaniesApi } from '@js-app/office/api/companies-api'

import { required, helpers } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import Loader from '@vue-app/components/base/Loader.vue'
import { mapGetters } from 'vuex'

import { BILL_TYPES_COMPANY_IDS } from '@vue-app/config'

const phoneOrEmail = (value) => {
  if (!value) return false
  const phonePattern = /^[0-9]{12}$/
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  return phonePattern.test(value) || emailPattern.test(value)
}

export default {
  name: 'CreateBill',
  props: ['id'],
  components: {
    Loader,
    CheckboxInput,
    CustomDatePicker,
    CustomSelect,
    PrimaryButton,
    TextInput,
    ArrowButton,
  },
  setup: () => ({ v$: useVuelidate() }),
  data() {
    return {
      billData: {
        phoneEmail: '',
        firstName: '',
        middleName: '',
        lastName: '',
        lang: 'uk',
        currency: 'uah',
        billNumber: '',
        sum: '',
        purpose: '',
        comment: '',
        createdAt: moment().toDate(),
        expAt: moment().add('1', 'weeks').toDate(),
        isCreateTemplate: false,
        templateName: '',
        billType: null,
      },
      billTypeTitle: '',
      billTypeOptions: [],
      langOptions: [
        { id: 'uk', name: 'create-bill.options.lang.uk' },
        { id: 'en', name: 'create-bill.options.lang.eng' },
      ],
      currencyOptions: [
        { id: 'uah', name: 'create-bill.options.currency.uah' },
      ],
      isPageLoading: false,
      billCreating: false,
      shouldProvidePhoneEmail: false,
      addBillType: false,
      numberMaskOptions: {
        mask: Number,
        radix: '.',
        thousandsSeparator: ' ',
        scale: 2,
        signed: false,
        normalizeZeros: true,
        padFractionalZeros: false,
        mapToRadix: [','],
      },
    }
  },

  created() {
    this.loadBillsSettings()

    this.billData.sum = this.NUMBER_MASK;

    this.$authorizer
      .can('read', 'bills-recipient_info_hide', this.userData.UserID.toString())
      .then((isCan) => {
        this.shouldProvidePhoneEmail = !isCan
        if (isCan) {
          this.billData.phoneEmail = this.$options.TEMPORARY_PHONE
        }
      })
  },

  validations() {
    let _validations = {
      billData: {
        firstName: { required },
        lastName: { required },
        sum: { required, minValue: 0.01 },
        purpose: { required },
        // phoneEmail: { required, phoneOrEmail: helpers.withMessage('Введіть номер телефону або email в правильному форматі', phoneOrEmail) }
      },
    }

    if (this.addBillType) {
      _validations.billData = {
        ..._validations.billData,
        billType: { required },
      }
    }

    if (this.shouldProvidePhoneEmail) {
      _validations.billData = {
        ..._validations.billData,
      }
    }

    return _validations
  },

  NUMBER_MASK: createNumberMask({
    prefix: '',
    allowDecimal: true,
    includeThousandsSeparator: true,
    thousandsSeparatorSymbol: ' ',
    allowNegative: false,
    decimalLimit: 2,
  }),

  TEMPORARY_PHONE: '380111111111',

  computed: {
    ...mapGetters(['userData']),
    backLinkRoute() {
      return RouteHelper.getRoute('bills-list', { companyId: this.id })
    },

    phoneEmailError() {
      if (this.shouldProvidePhoneEmail) {
        return !phoneOrEmail(this.billData.phoneEmail)
      }

      return null
    },

    phoneEmailValidation() {
      if (this.shouldProvidePhoneEmail) {
        return !this.phoneEmailError
      }
      return true
    },
  },

  watch: {
    billTypeOptions(newValue) {
      this.billTypeOptions = newValue
    },
  },

  methods: {
    async loadBillsSettings() {
      this.isPageLoading = true
      try {
        let _settings = await CompaniesApi.getBillsSettings(this.id)
        if (
          _settings.data &&
          _settings.data.bill_types &&
          _settings.data.bill_types.items
        ) {
          this.addBillType = true

          this.billTypeTitle = _settings.data.bill_types.name['uk'] ?? ''
          this.billTypeOptions = _settings.data.bill_types.items ?? []
        }
      } catch (e) {
        console.error(e)
      }

      this.isPageLoading = false
    },
    disabledDateHandler(val1) {
      return moment().diff(moment(val1), 'days') > 0
    },
    async createBill() {
      let formValid = await this.v$.$validate()

      if (!formValid) {
        return
      }

      if (this.billCreating) {
        return false
      }

      this.billCreating = true

      let _createBillResponse = null

      try {
        this.billData.expAt = moment(this.billData.expAt).endOf('day').toDate()

        _createBillResponse = await CompaniesApi.createBill(
          this.id,
          this.billData
        )
      } catch (e) {
        //TODO: Show error toast with error info
        return
      }

      await this.$router.push(
        RouteHelper.getRoute('bill-view', {
          companyId: this.id,
          billId: _createBillResponse.data.OperationID,
        })
      )
      this.billCreating = false
    },
  },
}
</script>

<style lang="scss" scoped>
.page {
  &--create-bill {
    padding-top: 28px;
  }

  &__header {
    margin-bottom: 24px;
  }
}
.create-bill {
  &__form {
    position: relative;
    background: #ffffff;
    border-radius: 22px;
    padding: 45px 50px 74px;
    width: 100%;
    max-width: 390px;

    &-loader {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;

      background: rgba(#ffffff, 0.7);
      z-index: 999;
    }

    &-fields {
      margin-bottom: 19px;
    }

    &-field {
      margin-bottom: 25px;

      &--row {
        display: flex;
        align-items: stretch;
      }

      &-small {
        width: 95px;
        margin-right: 26px;
        flex-shrink: 0;
        flex-grow: 0;
      }

      &-big {
        flex-shrink: 1;
        flex-grow: 1;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }

    &-template {
      margin-bottom: 38px;

      &-name {
        margin-top: 30px;
      }
    }

    &-action {
      padding: 25px 25px;
    }
  }
}
</style>
